import { useState, useMemo, useRef, useEffect } from 'react';
import MultiSelectFilter from './MultiSelectFilter';
import { downloadExcel } from '../utils/excelExport';

// Универсальная таблица: фильтр по каждому столбцу (множественный выбор значений) + сортировка
// по клику на заголовок. columns: [{ key, label, render?(row), filterValue?(row), sortValue?(row),
// sortable=true, filterable=true }]. Внутри столбца отмеченные значения объединяются через ИЛИ,
// между разными столбцами — через И (можно одновременно выбрать несколько проектов и несколько
// создателей заявки, например).
// filters/onFiltersChange — необязательные, для страниц, которым нужно пережить размонтирование
// (например, вернуться из карточки сделки к списку с тем же фильтром); без них таблица хранит
// фильтр сама, как и раньше.
// selectedIds/onSelectedChange — необязательные, включают чекбокс-столбец слева с чекбоксом
// «выбрать все» в шапке (выбирает/снимает только строки, видимые сейчас — с учётом фильтра).
// isRowSelectable(row) — необязательный предикат, отключает чекбокс у строк, которые ещё
// нельзя выбрать (например, пока не заполнены обязательные поля этой строки).
// canExport/exportFilename — необязательные, добавляют кнопку «Экспорт в Excel»: выгружает
// ровно то, что видно на экране (с учётом текущих фильтров и сортировки), тем же текстом,
// что и в ячейках таблицы.
function SelectAllCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} />;
}

export default function DataTable({ columns, rows, initialSort, onRowClick, emptyText = 'Ничего не найдено', filters: controlledFilters, onFiltersChange, selectedIds, onSelectedChange, isRowSelectable, canExport, exportFilename = 'Экспорт', rowClassName, renderSummary }) {
  const [internalFilters, setInternalFilters] = useState({});
  const filters = controlledFilters ?? internalFilters;
  const setFilters = onFiltersChange ?? setInternalFilters;
  const [sortKey, setSortKey] = useState(initialSort?.key || null);
  const [sortDir, setSortDir] = useState(initialSort?.dir || 'desc');

  const setFilter = (key, values) => setFilters(f => ({ ...f, [key]: values }));

  const cellText = (col, row) => {
    if (col.filterValue) return String(col.filterValue(row) ?? '');
    if (col.render) {
      const v = col.render(row);
      return typeof v === 'string' || typeof v === 'number' ? String(v) : String(row[col.key] ?? '');
    }
    return String(row[col.key] ?? '');
  };

  const filterableColumns = useMemo(() => columns.filter(c => c.filterable !== false), [columns]);

  const optionsByColumn = useMemo(() => {
    const map = {};
    for (const col of filterableColumns) {
      const set = new Set();
      for (const row of rows) set.add(cellText(col, row));
      map[col.key] = Array.from(set).sort((a, b) => {
        const na = Number(a), nb = Number(b);
        if (a !== '' && b !== '' && Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return a.localeCompare(b, 'ru');
      });
    }
    return map;
  }, [rows, filterableColumns]);

  // Сохранённые (например, между переходами по sessionStorage) значения фильтра могут
  // ссылаться на варианты, которых уже нет в текущих данных — тогда бейдж «Выбрано: N»
  // показывает счётчик, а строк не находится ни одной. Отсекаем такие значения здесь же,
  // чтобы и подсчёт результатов, и сам чекбокс-список видели только реально существующие варианты.
  const effectiveFilters = useMemo(() => {
    const result = {};
    for (const col of filterableColumns) {
      const stored = filters[col.key];
      if (!stored?.length) continue;
      const valid = optionsByColumn[col.key] || [];
      const sanitized = stored.filter(v => valid.includes(v));
      if (sanitized.length) result[col.key] = sanitized;
    }
    return result;
  }, [filters, filterableColumns, optionsByColumn]);

  const filtered = useMemo(() => {
    const active = filterableColumns.filter(c => effectiveFilters[c.key]?.length);
    if (!active.length) return rows;
    return rows.filter(row => active.every(col => effectiveFilters[col.key].includes(cellText(col, row))));
  }, [rows, effectiveFilters, filterableColumns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return filtered;
    const copy = [...filtered].sort((a, b) => {
      const av = col.sortValue ? col.sortValue(a) : a[sortKey];
      const bv = col.sortValue ? col.sortValue(b) : b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    if (sortDir === 'desc') copy.reverse();
    return copy;
  }, [filtered, sortKey, sortDir, columns]);

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    if (sortKey === col.key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(col.key); setSortDir('asc'); }
  };

  const selectable = selectedIds !== undefined && onSelectedChange !== undefined;
  const visibleIds = useMemo(
    () => sorted.filter(r => !isRowSelectable || isRowSelectable(r)).map(r => r.id),
    [sorted, isRowSelectable]
  );
  const visibleSelectedCount = selectable ? visibleIds.filter(id => selectedIds.includes(id)).length : 0;
  const allVisibleSelected = selectable && visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someVisibleSelected = selectable && visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      onSelectedChange(selectedIds.filter(id => !visibleIds.includes(id)));
    } else {
      onSelectedChange([...new Set([...selectedIds, ...visibleIds])]);
    }
  };

  const toggleRow = (id) => {
    onSelectedChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  };

  // Выгружаем ровно те столбцы с подписью (пустые "служебные" — чекбокс выбора, кнопки
  // действий — в экспорт не попадают) и ровно те строки, что сейчас видны на экране.
  const exportableColumns = columns.filter(c => c.label);
  const doExport = () => {
    downloadExcel(
      exportFilename,
      exportableColumns.map(c => c.label),
      sorted.map(row => exportableColumns.map(c => cellText(c, row)))
    );
  };

  // Столбцы с summable:true — считаем сумму по строкам, которые сейчас видны (после всех
  // фильтров и до пагинации, которой тут нет — таблица и так показывает всё разом). Числовое
  // значение берём через тот же sortValue, что и для сортировки — не заводим отдельный аксессор.
  const summableColumns = useMemo(() => columns.filter(c => c.summable), [columns]);
  const sums = useMemo(() => {
    const map = {};
    for (const col of summableColumns) {
      map[col.key] = sorted.reduce((s, row) => s + (Number(col.sortValue ? col.sortValue(row) : row[col.key]) || 0), 0);
    }
    return map;
  }, [sorted, summableColumns]);

  // На части систем (overlay-скроллбары, трекпады без shift+колесо) горизонтальный скролл
  // у широкой таблицы визуально никак не выдаёт себя — люди просто не понимают, что справа
  // есть ещё столбцы. Явно предупреждаем текстом, когда контент реально шире контейнера,
  // независимо от того, насколько заметна сама полоса прокрутки в конкретном браузере.
  const wrapRef = useRef(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const check = () => setScrollable(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener('resize', check);
    return () => { ro.disconnect(); window.removeEventListener('resize', check); };
  }, [sorted, columns]);

  return (
    <div className="table-wrap" ref={wrapRef}>
      {/* renderSummary — сводка над таблицей, считается по строкам, видимым сейчас (с учётом фильтров). */}
      {renderSummary && renderSummary(sorted)}
      {(canExport || scrollable) && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          {scrollable && (
            <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>
              ⟷ В таблице больше столбцов — прокрутите её вправо (полоса прокрутки внизу)
            </span>
          )}
          <div className="toolbar-spacer" />
          {canExport && <button className="btn-secondary btn-sm" onClick={doExport}>Экспорт в Excel</button>}
        </div>
      )}
      <table>
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 1 }}>
                <SelectAllCheckbox checked={allVisibleSelected} indeterminate={someVisibleSelected} onChange={toggleSelectAll} />
              </th>
            )}
            {columns.map(c => (
              <th key={c.key} onClick={() => toggleSort(c)} style={{ cursor: c.sortable === false ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
            ))}
          </tr>
          <tr>
            {selectable && <th />}
            {columns.map(c => (
              <th key={c.key} style={{ padding: '4px 6px' }} onClick={e => e.stopPropagation()}>
                {c.filterable !== false && (
                  <MultiSelectFilter
                    options={optionsByColumn[c.key] || []}
                    selected={effectiveFilters[c.key] || []}
                    onChange={values => setFilter(c.key, values)}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="empty-state">{emptyText}</td></tr>
          ) : sorted.map((row, i) => (
            <tr key={row.id ?? i} className={[onRowClick ? 'row-clickable' : '', rowClassName ? rowClassName(row) : ''].filter(Boolean).join(' ') || undefined} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {selectable && (
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    disabled={isRowSelectable ? !isRowSelectable(row) : false}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>
              )}
              {columns.map(c => <td key={c.key}>{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
        {summableColumns.length > 0 && sorted.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 600, borderTop: '2px solid var(--border)' }}>
              {selectable && <td />}
              {columns.map((c, i) => (
                <td key={c.key}>
                  {summableColumns.some(sc => sc.key === c.key)
                    ? (c.footerFormat ? c.footerFormat(sums[c.key]) : Math.round(sums[c.key]).toLocaleString('ru'))
                    : (i === 0 ? 'Итого' : '')}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      <div className="text-muted" style={{ marginTop: 8, fontSize: 12 }}>Показано: {sorted.length} из {rows.length}</div>
    </div>
  );
}
